import type { DamageZone } from "./types"

// Shared top-down vehicle geometry. The interactive tap-map in
// step-damage-mark.tsx carries its own inline copy of these coordinates;
// the read-only AHA reveal (annotated-vehicle.tsx) consumes this module so the
// two silhouettes match. If you retune one, retune the other (a dev
// re-implementing on main should consolidate these into one source).

export const VEHICLE_VIEWBOX = "0 0 220 280"

export const BODY_PATH =
  "M 60 22 Q 60 8 78 6 Q 110 2 142 6 Q 160 8 160 22 L 160 258 Q 160 272 142 274 Q 110 278 78 274 Q 60 272 60 258 Z"

export const GLASS_PATH =
  "M 70 70 Q 70 62 82 60 L 138 60 Q 150 62 150 70 L 150 200 Q 150 208 138 210 L 82 210 Q 70 208 70 200 Z"

// Always-visible panel seams [x1,y1,x2,y2].
export const SEAMS: ReadonlyArray<[number, number, number, number]> = [
  [60, 60, 160, 60],
  [70, 22, 70, 60],
  [150, 22, 150, 60],
  [60, 135, 160, 135],
  [60, 200, 160, 200],
  [60, 210, 160, 210],
  [60, 248, 160, 248],
  [70, 60, 70, 248],
  [150, 60, 150, 248],
]

export interface ZoneGeo {
  d: string
  label: string
  labelX: number
  labelY: number
  rotated?: boolean
  glass?: boolean
}

// Draw order matters (later zones paint on top). Keep this array ordered.
export const ZONE_ORDER: DamageZone[] = [
  "hood",
  "windshield",
  "roof",
  "front-fender-left",
  "front-fender-right",
  "front-door-left",
  "front-door-right",
  "rear-door-left",
  "rear-door-right",
  "quarter-panel-left",
  "quarter-panel-right",
  "trunk",
  "rear-bumper",
  "front-bumper",
]

export const ZONE_GEO: Record<DamageZone, ZoneGeo> = {
  hood: {
    d: "M 60 22 Q 60 8 78 6 Q 110 2 142 6 Q 160 8 160 22 L 160 60 L 60 60 Z",
    label: "Hood",
    labelX: 110,
    labelY: 36,
  },
  windshield: {
    d: "M 70 60 L 150 60 L 150 78 L 70 78 Z",
    label: "Windshield",
    labelX: 110,
    labelY: 71,
    glass: true,
  },
  roof: {
    d: "M 70 78 L 150 78 L 150 192 L 70 192 Z",
    label: "Roof",
    labelX: 110,
    labelY: 134,
    glass: true,
  },
  "front-fender-left": {
    d: "M 60 22 Q 60 8 78 6 L 78 60 L 60 60 Z",
    label: "L. fender",
    labelX: 68,
    labelY: 42,
  },
  "front-fender-right": {
    d: "M 142 6 Q 160 8 160 22 L 160 60 L 142 60 Z",
    label: "R. fender",
    labelX: 152,
    labelY: 42,
  },
  "front-door-left": {
    d: "M 60 60 L 70 60 L 70 135 L 60 135 Z",
    label: "L. door",
    labelX: 66,
    labelY: 100,
    rotated: true,
  },
  "front-door-right": {
    d: "M 150 60 L 160 60 L 160 135 L 150 135 Z",
    label: "R. door",
    labelX: 155,
    labelY: 100,
    rotated: true,
  },
  "rear-door-left": {
    d: "M 60 135 L 70 135 L 70 200 L 60 200 Z",
    label: "L. rear",
    labelX: 66,
    labelY: 170,
    rotated: true,
  },
  "rear-door-right": {
    d: "M 150 135 L 160 135 L 160 200 L 150 200 Z",
    label: "R. rear",
    labelX: 155,
    labelY: 170,
    rotated: true,
  },
  "quarter-panel-left": {
    d: "M 60 200 L 70 200 L 70 248 L 60 248 Z",
    label: "L. quarter",
    labelX: 66,
    labelY: 224,
    rotated: true,
  },
  "quarter-panel-right": {
    d: "M 150 200 L 160 200 L 160 248 L 150 248 Z",
    label: "R. quarter",
    labelX: 155,
    labelY: 224,
    rotated: true,
  },
  trunk: {
    d: "M 70 210 L 150 210 L 150 248 L 70 248 Z",
    label: "Trunk",
    labelX: 110,
    labelY: 230,
  },
  "rear-bumper": {
    d: "M 60 248 L 160 248 L 160 258 Q 160 272 142 274 Q 110 278 78 274 Q 60 272 60 258 Z",
    label: "Rear bumper",
    labelX: 110,
    labelY: 264,
  },
  "front-bumper": {
    d: "M 60 22 Q 60 8 78 6 Q 110 2 142 6 Q 160 8 160 22 L 160 14 Q 160 0 142 -2 Q 110 -6 78 -2 Q 60 0 60 14 Z",
    label: "Front bumper",
    labelX: 110,
    labelY: 14,
  },
}
